import { toWorldPixel } from './webMercator'

interface CollideByRadiusParams<TItem, TId> {
  items: TItem[]
  zoom: number
  radiusPx: number
  offsetY?: number
  getId: (item: TItem) => TId
  getPosition: (item: TItem) => readonly [number, number] | null | undefined
  keepIds?: ReadonlySet<TId> | null
}

/**
 * Screen-space collision walk over a spatial hash: an item is accepted when it
 * clears every already-accepted neighbour in the 3×3 cell window. Items are
 * visited last-to-first, so higher-priority entries (sorted last) claim their
 * cell first; `keepIds` bypasses the check entirely but still occupies a cell.
 */
export function collideByRadius<TItem, TId>({
  items,
  zoom,
  radiusPx,
  offsetY = 0,
  getId,
  getPosition,
  keepIds = null,
}: CollideByRadiusParams<TItem, TId>): Set<TId> {
  const radiusSq = radiusPx * radiusPx
  const cellPx = radiusPx || 1
  const accepted = new Set<TId>()
  const grid = new Map<string, { px: number; py: number }[]>()

  for (let index = items.length - 1; index >= 0; index--) {
    const item = items[index]
    const position = getPosition(item)
    if (position) {
      const [lng, lat] = position
      const [px, worldPy] = toWorldPixel(lng, lat, zoom)
      const py = worldPy + offsetY
      const cellX = Math.floor(px / cellPx)
      const cellY = Math.floor(py / cellPx)
      const id = getId(item)

      const overlaps =
        !(keepIds && keepIds.has(id)) &&
        [-1, 0, 1].some(cellOffsetX =>
          [-1, 0, 1].some(cellOffsetY => {
            const bucket = grid.get(`${cellX + cellOffsetX},${cellY + cellOffsetY}`)
            if (!bucket) return false
            return bucket.some(other => {
              const deltaX = px - other.px
              const deltaY = py - other.py
              return deltaX * deltaX + deltaY * deltaY < radiusSq
            })
          })
        )

      if (!overlaps) {
        const key = `${cellX},${cellY}`
        const bucket = grid.get(key)
        if (bucket) bucket.push({ px, py })
        else grid.set(key, [{ px, py }])
        accepted.add(id)
      }
    }
  }

  return accepted
}
